import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Trophy, X, AlertTriangle, Sparkles, RefreshCw } from "lucide-react";

/**
 * Tender Result + Bid Score panel (per tender_result_upload_bidscore_spec.md).
 * Lives inside the Bid Score tab. Lets the user upload the public bidder list,
 * shows it as a table with their row highlighted, and computes a position-based
 * Bid Score (((N - rank) / (N - 1)) * 100). Won/Lost is NOT auto-changed.
 */

function TierPill({ tier }) {
  if (!tier) return null;
  const c = tier.color;
  const cls =
    c === "dark_green" ? "bg-good-soft text-good border-good/50"
    : c === "green"      ? "bg-good-soft/70 text-good border-good/30"
    : c === "amber"      ? "bg-warn-soft text-warn border-warn/40"
    : c === "orange"     ? "bg-warn-soft/90 text-warn-strong border-warn/60"
    : c === "red"        ? "bg-red-50 text-red-700 border-red-300"
    : "bg-secondary text-ink border-ink-line";
  return <span className={`chip border ${cls} text-[10px] font-display font-bold tracking-tight`}>{tier.band}</span>;
}

function fmtBid(value, currency) {
  if (value == null) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${currency ? currency + " " : ""}${n.toLocaleString("en-IN")}`;
}

function fmtPct(p) {
  if (p == null) return "—";
  const n = Number(p);
  if (Number.isNaN(n)) return String(p);
  return `${n.toFixed(n === 0 ? 0 : 1)}%`;
}

export default function TenderResultPanel({ tenderId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenders/${tenderId}/result`);
      setResult(data.result);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenderId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/result/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.match_status === "matched") {
        toast.success(`Matched your row — ${data.user_position} of ${data.total_bidders} · Bid Score ${data.bid_score}`);
      } else if (data.match_status === "no_company_name") {
        toast.warning("Add your Company Name in Profile so we can match your row automatically. Manual pick is available below.");
      } else {
        toast.warning("We couldn't match your row by company name. Pick your row manually below.");
      }
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setUploading(false); }
  };

  const pickRow = async (idx) => {
    try {
      const { data } = await api.patch(`/tenders/${tenderId}/result/pick-row`, { user_row_index: idx });
      setResult(data);
      toast.success(`Selected ${data.user_position} of ${data.total_bidders} · Bid Score ${data.bid_score}`);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const removeResult = async () => {
    try {
      await api.delete(`/tenders/${tenderId}/result`);
      setResult(null);
      toast.success("Tender result removed");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (loading) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading tender result…</div>;

  if (!result) {
    return (
      <div className="card-flat p-6" data-testid="tender-result-empty">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold tracking-tight">Upload tender result</div>
            <p className="text-sm text-ink-muted mt-1">
              Once the issuing authority publishes the bidder list, upload the Excel sheet here. We'll parse it, highlight your row, and compute your Bid Score.
            </p>
            <p className="text-[11px] text-ink-muted mt-2">Won/Lost state is NOT changed automatically — you stay in control.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" data-testid="tender-result-upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="tender-result-upload-btn"
              className="mt-4 px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm inline-flex items-center gap-2 disabled:opacity-50 transition-colors">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> Upload Result Sheet</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const noMatch = result.match_status === "no_match" || result.match_status === "no_company_name";
  return (
    <div className="space-y-5" data-testid="tender-result-panel">
      {/* Header info + Bid Score */}
      <div className="card-flat p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="stat-label">Tender Result</div>
            <div className="font-display font-bold tracking-tight text-lg mt-1">
              {result.tender_title || "—"}
            </div>
            <div className="text-xs text-ink-muted mt-1 font-num">
              {result.client_name && <span>{result.client_name}</span>}
              {result.result_tender_no && <span> · {result.result_tender_no}</span>}
              {result.closing_date && <span> · Closing {result.closing_date}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" data-testid="tender-result-reupload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="tender-result-reupload-btn"
              className="px-3 py-1.5 text-xs border border-ink-line hover:bg-secondary rounded-sm inline-flex items-center gap-1.5 transition-colors disabled:opacity-50">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Replace
            </button>
            <button onClick={removeResult} data-testid="tender-result-delete-btn"
              className="px-3 py-1.5 text-xs border border-ink-line text-warn hover:bg-warn-soft rounded-sm inline-flex items-center gap-1.5 transition-colors">
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-ink-line">
          <div>
            <div className="stat-label">Your Position</div>
            <div className="font-num font-bold text-3xl mt-1" data-testid="tender-result-position">
              {result.user_position || "—"}{result.user_position && <span className="text-ink-muted text-base font-normal"> of {result.total_bidders}</span>}
            </div>
          </div>
          <div>
            <div className="stat-label">Position Score</div>
            <div className="text-[10px] text-ink-muted uppercase tracking-wider mt-0.5">From result upload · informational</div>
            <div className="font-num font-bold text-3xl mt-1 text-brand" data-testid="tender-result-bidscore">
              {result.bid_score != null ? `${result.bid_score}` : "—"}{result.bid_score != null && <span className="text-ink-muted text-base font-normal"> / 100</span>}
            </div>
            {result.bid_score_tier && <div className="mt-2"><TierPill tier={result.bid_score_tier} /></div>}
            <div className="text-[10px] text-ink-muted mt-1.5">Note: The Bid Score on the Overview = your TAPI score.</div>
          </div>
          <div>
            <div className="stat-label">Match status</div>
            <div className="text-sm font-medium mt-1.5" data-testid="tender-result-match-status">
              {result.match_status === "matched"   && <span className="text-good">Matched on company name</span>}
              {result.match_status === "manual"    && <span className="text-brand">Manually selected</span>}
              {result.match_status === "no_match"  && <span className="text-warn">No name match — pick below</span>}
              {result.match_status === "no_company_name" && <span className="text-warn">Set company name in Profile</span>}
            </div>
            {result.parse_warnings?.length > 0 && (
              <div className="text-[11px] text-warn mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {result.parse_warnings.join(" · ")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Bidder table */}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm" data-testid="tender-result-table">
          <thead className="bg-secondary/60 text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="text-left px-3 py-2 w-12">Sr</th>
              <th className="text-left px-3 py-2">Bidder Name</th>
              <th className="text-left px-3 py-2 w-16">Cur</th>
              <th className="text-right px-3 py-2 w-32">Bid Value</th>
              <th className="text-center px-3 py-2 w-14">Pos</th>
              <th className="text-right px-3 py-2 w-20">%Diff</th>
              {noMatch && <th className="w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {(result.rows || []).map((r, i) => {
              const me = r.is_current_user;
              return (
                <tr key={i} data-testid={`tender-result-row-${i}`}
                  className={`border-t border-ink-line ${me ? "bg-brand-soft border-l-4 border-l-brand font-medium" : "hover:bg-secondary/40"}`}>
                  <td className="px-3 py-2 font-num text-ink-muted text-xs">{r.sr_no ?? "—"}</td>
                  <td className="px-3 py-2">{me && <Trophy className="inline w-3.5 h-3.5 text-brand mr-1.5 -mt-0.5" />}{r.bidder_name}{me && <span className="ml-2 chip border-brand/40 bg-white text-brand text-[9px]">YOU</span>}</td>
                  <td className="px-3 py-2 text-ink-muted">{r.currency || "—"}</td>
                  <td className="px-3 py-2 text-right font-num" data-testid={`tender-result-bid-value-${i}`}>{fmtBid(r.bid_value, r.currency)}</td>
                  <td className="px-3 py-2 text-center font-num font-semibold" data-testid={`tender-result-position-${i}`}>{r.position || `L${r.position_rank}`}</td>
                  <td className="px-3 py-2 text-right font-num text-ink-muted">{fmtPct(r.pct_difference_from_l1)}</td>
                  {noMatch && (
                    <td className="px-3 py-2 text-right">
                      {!me && (
                        <button onClick={() => pickRow(i)} data-testid={`tender-result-pick-${i}`}
                          className="text-xs text-brand hover:underline">
                          Pick as me
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
